import { ipdApi } from "@/api/ipd";
import { labApi } from "@/api/lab";
import { Button } from "@/components/ui/button";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { useEffect, useMemo, useState, useRef } from "react";
import { Search, Upload, FileText, X, Eye, TestTube } from "lucide-react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/contexts/AuthContext";

type IPDLabTestManagerProps = {
    /** Hospital admin view: edit prices on completed tests; no lab workflow actions. */
    adminMode?: boolean;
};

export default function IPDLabTestManager({ adminMode = false }: IPDLabTestManagerProps) {
    const queryClient = useQueryClient();
    const { user, activeHospitalId } = useAuth();
    const reportHospitalId =
        adminMode && (activeHospitalId || user?.hospitalId)
            ? activeHospitalId || user?.hospitalId || undefined
            : undefined;
    const [activeView, setActiveView] = useState<"patients" | "tests">(
        adminMode ? "tests" : "patients"
    );
    const [selectedTest, setSelectedTest] = useState<any | null>(null);
    const [isResultDialogOpen, setIsResultDialogOpen] = useState(false);
    const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
    const [editTest, setEditTest] = useState<any | null>(null);
    const [editProcessingType, setEditProcessingType] = useState<"INTERNAL" | "EXTERNAL">("INTERNAL");
    const [editExternalVendorId, setEditExternalVendorId] = useState<string>("");
    const [editExternalCost, setEditExternalCost] = useState<string>("");
    /** Patient-facing amount on IPD bill (same as reception billing line). */
    const [editCustomerPrice, setEditCustomerPrice] = useState<string>("");
    const [statusFilter, setStatusFilter] = useState<string>('all');
    const [processingFilter, setProcessingFilter] = useState<'all' | 'INTERNAL' | 'EXTERNAL'>('all');
    const [uploadedFiles, setUploadedFiles] = useState<Array<{ id: string; name: string; url: string; file?: File }>>([]);
    const [isUploading, setIsUploading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [patientSearchInput, setPatientSearchInput] = useState('');
    const [patientSearchQuery, setPatientSearchQuery] = useState('');
    const fileInputRef = useRef<HTMLInputElement>(null);

    const [resultData, setResultData] = useState({
        resultValue: '',
        resultUnit: '',
        normalRange: '',
        abnormalFlag: false,
        resultNotes: '',
    });

    // Admitted IPD patients (for lab technician ordering tests)
    const { data: admittedAdmissions = [] } = useQuery<any[]>({
        queryKey: ["ipd-admissions-for-lab"],
        queryFn: async () => {
            // Include discharged admissions too (late-entry / corrections may happen after discharge).
            const res = await ipdApi.getAdmissions();
            return res.data?.data || [];
        },
    });

    // Lab master tests
    const { data: labTestsMaster = [] } = useQuery<any[]>({
        queryKey: ["lab-tests-master"],
        queryFn: async () => {
            const res = await labApi.getLabTests();
            const raw = res.data as any;
            return Array.isArray(raw?.data) ? raw.data : raw?.data || [];
        },
    });

    const [orderDialogOpen, setOrderDialogOpen] = useState(false);
    const [selectedAdmission, setSelectedAdmission] = useState<any | null>(null);
    const [selectedLabTestIds, setSelectedLabTestIds] = useState<string[]>([]);
    const [orderTestSearch, setOrderTestSearch] = useState("");
    const [orderPriority, setOrderPriority] = useState<"ROUTINE" | "URGENT" | "STAT">("ROUTINE");
    const [ordering, setOrdering] = useState(false);
    const [orderProcessingType, setOrderProcessingType] = useState<"INTERNAL" | "EXTERNAL">("INTERNAL");
    const [selectedExternalVendorId, setSelectedExternalVendorId] = useState<string>("");
    const [externalCostByTestId, setExternalCostByTestId] = useState<Record<string, string>>({});

    // External lab vendors
    const { data: externalVendors = [], refetch: refetchExternalVendors } = useQuery<any[]>({
        queryKey: ["ipd-lab-external-vendors", reportHospitalId ?? "default"],
        queryFn: async () => {
            const res = await ipdApi.listLabExternalVendors(
                reportHospitalId ? { hospitalId: reportHospitalId } : undefined
            );
            return res.data?.data || [];
        },
        staleTime: 300000,
        enabled: !adminMode || !!reportHospitalId,
    });

    const [vendorDialogOpen, setVendorDialogOpen] = useState(false);
    const [newVendorForm, setNewVendorForm] = useState({ name: "", phone: "", address: "", gstNo: "", email: "" });
    const [creatingVendor, setCreatingVendor] = useState(false);

    const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false);
    const [invoiceVendorId, setInvoiceVendorId] = useState<string>("");
    const [invoiceForm, setInvoiceForm] = useState({ invoiceNumber: "", invoiceDate: "", totalAmount: "", notes: "" });
    const [invoiceFile, setInvoiceFile] = useState<File | null>(null);
    const [uploadingInvoice, setUploadingInvoice] = useState(false);

    const { data: ipdLabTests, isLoading, error } = useQuery<any>({
        queryKey: ['ipd-lab-tests', statusFilter, reportHospitalId ?? 'default'],
        queryFn: async () => {
            console.log('Fetching IPD lab tests with status filter:', statusFilter);
            const response = await ipdApi.getIPDLabTestsByHospital({
                status: statusFilter !== 'all' ? statusFilter : undefined,
                ...(reportHospitalId ? { hospitalId: reportHospitalId } : {}),
            });
            console.log('IPD lab tests response:', response.data);
            return response.data?.data || [];
        },
        enabled: !adminMode || !!reportHospitalId,
    });

    if (error) {
        console.error('Error fetching IPD lab tests:', error);
    }

    const updateTestStatusMutation = useMutation({
        mutationFn: async ({ testId, status }: { testId: string; status: string }) => {
            const response = await ipdApi.updateIPDLabTest(testId, { status });
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['ipd-lab-tests'] });
            toast.success('Test status updated successfully');
        },
        onError: (error: any) => {
            const errorMessage = error.response?.data?.message || error.message || 'Failed to update test status';
            toast.error(errorMessage);
            console.error('Error updating test status:', error);
        },
    });

    const deleteTestMutation = useMutation({
        mutationFn: async (testId: string) => {
            const response = await ipdApi.deleteIPDLabTest(testId);
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['ipd-lab-tests'] });
            toast.success('Test deleted');
        },
        onError: (error: any) => {
            const errorMessage = error.response?.data?.message || error.message || 'Failed to delete test';
            toast.error(errorMessage);
        },
    });

    const updateTestResultMutation = useMutation({
        mutationFn: async ({ testId, data }: { testId: string; data: any }) => {
            const userId = localStorage.getItem('userId');
            const response = await ipdApi.updateIPDLabTest(testId, {
                ...data,
                resultDate: new Date().toISOString(),
                completedAt: new Date().toISOString(),
                status: 'COMPLETED',
                performedById: userId || undefined,
            });
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['ipd-lab-tests'] });
            toast.success('Test result saved successfully');
            setIsResultDialogOpen(false);
            setSelectedTest(null);
            setResultData({
                resultValue: '',
                resultUnit: '',
                normalRange: '',
                abnormalFlag: false,
                resultNotes: '',
            });
            setUploadedFiles([]);
            setIsUploading(false);
        },
        onError: (error) => {
            toast.error('Failed to save test result');
            console.error('Error saving test result:', error);
        },
    });

    const updateTestMetaMutation = useMutation({
        mutationFn: async (payload: {
            id: string;
            data: {
                processingType?: 'INTERNAL' | 'EXTERNAL';
                externalVendorId?: string | null;
                externalCost?: number | null;
                externalInvoiceId?: string | null;
                testCost?: number | null;
            };
        }) => {
            const response = await ipdApi.updateIPDLabTest(payload.id, payload.data);
            return response.data;
        },
        onSuccess: (payload: any) => {
            queryClient.invalidateQueries({ queryKey: ['ipd-lab-tests'] });
            const aid = payload?.data?.admissionId as string | undefined;
            if (aid) {
                queryClient.invalidateQueries({ queryKey: ['ipd-bill-details', aid] });
            }
            toast.success('Test updated');
            setIsEditDialogOpen(false);
            setEditTest(null);
        },
        onError: (error: any) => {
            const errorMessage = error.response?.data?.message || error.message || 'Failed to update test';
            toast.error(errorMessage);
        },
    });

    const uploadAttachmentMutation = useMutation({
        mutationFn: async ({ file, labTestId }: { file: File; labTestId: string }) => {
            console.log('Uploading attachment for lab test:', labTestId, 'File:', file.name);
            const response = await ipdApi.uploadIPDLabTestAttachment(labTestId, file);
            console.log('Upload response:', response);
            return response.data;
        },
        onSuccess: (data) => {
            const attachment = data.data;
            // Update the file in state to mark it as uploaded (remove the File object)
            setUploadedFiles(prev => prev.map(f => 
                f.name === attachment.fileName && f.file 
                    ? { id: attachment.id, name: attachment.fileName || 'Unknown', url: attachment.fileUrl }
                    : f
            ));
        },
        onError: (error: any) => {
            const errorMessage = error.response?.data?.message || error.message || 'Failed to upload document';
            toast.error(errorMessage);
            console.error('Error uploading document:', error);
            console.error('Error response:', error.response?.data);
        },
    });

    const deleteAttachmentMutation = useMutation({
        mutationFn: async (attachmentId: string) => {
            const res = await ipdApi.deleteIPDLabTestAttachment(attachmentId);
            return res.data;
        },
        onSuccess: () => {
            toast.success('Document deleted');
        },
        onError: (error: any) => {
            const msg = error?.response?.data?.message || error?.message || 'Failed to delete document';
            toast.error(msg);
        },
    });

    const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file || !selectedTest) return;

        // Add file to local state only (not uploaded yet)
        const tempId = `temp-${Date.now()}-${Math.random()}`;
        setUploadedFiles(prev => [...prev, {
            id: tempId,
            name: file.name,
            url: '',
            file: file
        }]);
        
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleRemoveFile = (fileId: string) => {
        const f = uploadedFiles.find((x) => x.id === fileId);
        // If it's a local (not-yet-uploaded) file, just remove from UI.
        if (f?.file) {
            setUploadedFiles((prev) => prev.filter((x) => x.id !== fileId));
            return;
        }
        // Otherwise delete persisted attachment from backend.
        deleteAttachmentMutation.mutate(fileId, {
            onSuccess: () => {
                setUploadedFiles((prev) => prev.filter((x) => x.id !== fileId));
            },
        });
    };

    const handleStatusUpdate = (test: any) => {
        if (test.status === 'ORDERED') {
            updateTestStatusMutation.mutate({
                testId: test.id,
                status: 'IN_PROGRESS'
            });
        } else if (test.status === 'IN_PROGRESS' || test.status === 'SCHEDULED') {
            setSelectedTest(test);
            setResultData({
                resultValue: test.resultValue || '',
                resultUnit: test.resultUnit || '',
                normalRange: test.normalRange || '',
                abnormalFlag: test.abnormalFlag || false,
                resultNotes: test.resultNotes || '',
            });
            setUploadedFiles(test.attachments?.map((att: any) => ({
                id: att.id,
                name: att.fileName,
                url: att.fileUrl
            })) || []);
            setIsResultDialogOpen(true);
        }
    };

    const handleSubmitResult = async () => {
        if (!selectedTest) return;

        if (!resultData.resultValue.trim()) {
            toast.error('Result value is required');
            return;
        }

        // Upload all files that haven't been uploaded yet (have a File object)
        const filesToUpload = uploadedFiles.filter(f => f.file);
        
        if (filesToUpload.length > 0) {
            setIsUploading(true);
            try {
                // Upload all files in parallel
                await Promise.all(
                    filesToUpload.map(fileEntry => 
                        uploadAttachmentMutation.mutateAsync({ 
                            file: fileEntry.file!, 
                            labTestId: selectedTest.id 
                        })
                    )
                );
                setIsUploading(false);
            } catch (error) {
                setIsUploading(false);
                toast.error('Failed to upload some documents');
                console.error('Error uploading documents:', error);
                return; // Don't proceed with saving test results if file uploads fail
            }
        }

        // Now save the test results
        updateTestResultMutation.mutate({
            testId: selectedTest.id,
            data: resultData
        });
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'ORDERED': return 'bg-yellow-100 text-yellow-800';
            case 'SCHEDULED': return 'bg-blue-100 text-blue-800';
            case 'IN_PROGRESS': return 'bg-blue-100 text-blue-800';
            case 'COMPLETED': return 'bg-green-100 text-green-800';
            case 'CANCELLED': return 'bg-red-100 text-red-800';
            case 'FAILED': return 'bg-red-100 text-red-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    const getPriorityColor = (priority: string) => {
        switch (priority) {
            case 'URGENT': return 'bg-red-100 text-red-800';
            case 'STAT': return 'bg-purple-100 text-purple-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    const filteredAdmittedAdmissions = admittedAdmissions.filter((adm: any) => {
        const status = String(adm?.status ?? '').toUpperCase();
        if (status && status !== 'ADMITTED') return false;

        if (!patientSearchQuery.trim()) return true;
        const searchLower = patientSearchQuery.toLowerCase();
        const patientName = String(adm?.queue?.patient?.name ?? '').toLowerCase();
        const uhid = String(adm?.queue?.patient?.uhid ?? '').toLowerCase();
        const ipdNumber = String(adm?.queue?.ipdNumber ?? adm?.id ?? '').toLowerCase();
        const ward = String(adm?.wardType ?? '').toLowerCase();
        const bed = String(adm?.bedNumber ?? '').toLowerCase();

        return (
            patientName.includes(searchLower) ||
            uhid.includes(searchLower) ||
            ipdNumber.includes(searchLower) ||
            ward.includes(searchLower) ||
            bed.includes(searchLower)
        );
    });

    const filteredTests = (ipdLabTests || [])
        .filter((test: any) => {
            if (processingFilter === "all") return true;
            const t = String(test.processingType ?? "INTERNAL").toUpperCase();
            return t === processingFilter;
        })
        .filter((test: any) => {
        if (!searchQuery.trim()) return true;
        const searchLower = searchQuery.toLowerCase();
        return (
            test.testName?.toLowerCase().includes(searchLower) ||
            test.admission?.queue?.patient?.name?.toLowerCase().includes(searchLower) ||
            test.admission?.queue?.patient?.uhid?.toLowerCase().includes(searchLower) ||
            test.status?.toLowerCase().includes(searchLower)
        );
    });

    const externalVendorsById = useMemo(() => {
        const m = new Map<string, any>();
        for (const v of externalVendors) m.set(v.id, v);
        return m;
    }, [externalVendors]);

    useEffect(() => {
        if (!isEditDialogOpen) return;
        if (editProcessingType === "INTERNAL") {
            setEditExternalVendorId("");
            setEditExternalCost("");
        }
    }, [isEditDialogOpen, editProcessingType]);

    useEffect(() => {
        if (orderProcessingType === "INTERNAL") {
            setSelectedExternalVendorId("");
            setExternalCostByTestId({});
        }
    }, [orderProcessingType]);

    const filteredLabMasterTests = useMemo(() => {
        const term = orderTestSearch.trim().toLowerCase();
        if (!term) return labTestsMaster;
        return (labTestsMaster ?? []).filter((t: any) => {
            const name = String(t?.name ?? "").toLowerCase();
            const code = String(t?.code ?? "").toLowerCase();
            return name.includes(term) || code.includes(term);
        });
    }, [labTestsMaster, orderTestSearch]);

    return (
        <div className="p-6">
            <div className="flex justify-between items-center mb-6">
                <h1 className="font-artis-serif text-2xl font-semibold text-artis-brown-dark flex items-center gap-2">
                    <TestTube className="h-6 w-6 text-blue-600" />
                    {adminMode ? "IPD Lab Tests — Admin" : "IPD Lab – Technician"}
                </h1>
            </div>

            {adminMode ? (
                <p className="text-sm text-muted-foreground mb-4 max-w-3xl">
                    Review and correct hospital cost and patient price on IPD lab orders (including completed tests).
                    Saved amounts update the lab record and sync to the IPD revenue bill when possible.
                </p>
            ) : null}

            {!adminMode ? (
                <div className="flex gap-3 mb-4">
                    <Button
                        variant={activeView === "patients" ? "default" : "outline"}
                        size="sm"
                        onClick={() => setActiveView("patients")}
                    >
                        Admitted patients
                    </Button>
                    <Button
                        variant={activeView === "tests" ? "default" : "outline"}
                        size="sm"
                        onClick={() => setActiveView("tests")}
                    >
                        All IPD tests
                    </Button>
                </div>
            ) : null}

            {adminMode && !reportHospitalId ? (
                <div className="rounded-lg border bg-amber-50 text-amber-900 px-4 py-3 text-sm mb-4">
                    Select a hospital from the header to load IPD lab tests.
                </div>
            ) : null}

            {!adminMode && activeView === "patients" ? (
                <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
                    <div className="p-4 border-b flex items-center justify-between">
                        <h2 className="font-medium">Current admitted IPD patients</h2>
                        {isLoading && (
                            <span className="text-xs text-gray-500">Loading tests…</span>
                        )}
                    </div>
                    <div className="p-4 border-b bg-gray-50/60">
                        <div className="flex flex-col md:flex-row gap-2 md:items-center">
                            <div className="relative flex-1">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                                <Input
                                    type="text"
                                    placeholder="Search admitted IPD patient by name, UHID, or IPD ID..."
                                    value={patientSearchInput}
                                    onChange={(e) => setPatientSearchInput(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                            setPatientSearchQuery(patientSearchInput.trim());
                                        }
                                    }}
                                    className="pl-10"
                                />
                            </div>
                            <div className="flex gap-2">
                                <Button
                                    type="button"
                                    onClick={() => setPatientSearchQuery(patientSearchInput.trim())}
                                >
                                    <Search className="h-4 w-4 mr-2" />
                                    Search
                                </Button>
                                {patientSearchQuery && (
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={() => {
                                            setPatientSearchInput('');
                                            setPatientSearchQuery('');
                                        }}
                                    >
                                        Clear
                                    </Button>
                                )}
                            </div>
                        </div>
                    </div>
                    {filteredAdmittedAdmissions.length === 0 ? (
                        <div className="p-6 text-center text-gray-500">
                            {patientSearchQuery
                                ? "No currently admitted IPD patients match your search."
                                : "No admitted IPD patients found."}
                        </div>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Patient</TableHead>
                                    <TableHead>UHID</TableHead>
                                    <TableHead>IPD ID</TableHead>
                                    <TableHead>Ward</TableHead>
                                    <TableHead>Bed</TableHead>
                                    <TableHead>Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {filteredAdmittedAdmissions.map((adm: any) => (
                                    <TableRow key={adm.id}>
                                        <TableCell className="font-medium">
                                            {adm.queue?.patient?.name ?? "N/A"}
                                        </TableCell>
                                        <TableCell>{adm.queue?.patient?.uhid ?? "N/A"}</TableCell>
                                        <TableCell>{adm.queue?.ipdNumber ?? adm.id}</TableCell>
                                        <TableCell>{adm.wardType}</TableCell>
                                        <TableCell>{adm.bedNumber ?? "—"}</TableCell>
                                        <TableCell>
                                            <Button
                                                size="sm"
                                                onClick={() => {
                                                    setSelectedAdmission(adm);
                                                    setSelectedLabTestIds([]);
                                                    setOrderPriority("ROUTINE");
                                                    setOrderDialogOpen(true);
                                                }}
                                            >
                                                Order tests
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </div>
            ) : (
                <>
                    <div className="flex gap-4 mb-6">
                        <div className="relative flex-1 max-w-sm">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                            <Input
                                type="text"
                                placeholder="Search by patient name, test name, or UHID..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="pl-10"
                            />
                        </div>
                        <Select value={processingFilter} onValueChange={(v: any) => setProcessingFilter(v)}>
                            <SelectTrigger className="w-[190px]">
                                <SelectValue placeholder="Internal/External" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All (Internal + External)</SelectItem>
                                <SelectItem value="INTERNAL">Internal</SelectItem>
                                <SelectItem value="EXTERNAL">External</SelectItem>
                            </SelectContent>
                        </Select>
                        <Select value={statusFilter} onValueChange={setStatusFilter}>
                            <SelectTrigger className="w-[180px]">
                                <SelectValue placeholder="Filter by status" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All Tests</SelectItem>
                                <SelectItem value="ORDERED">Ordered</SelectItem>
                                <SelectItem value="SCHEDULED">Scheduled</SelectItem>
                                <SelectItem value="IN_PROGRESS">In Progress</SelectItem>
                                <SelectItem value="COMPLETED">Completed</SelectItem>
                                <SelectItem value="CANCELLED">Cancelled</SelectItem>
                                <SelectItem value="FAILED">Failed</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>

                    {isLoading ? (
                        <div className="text-center py-8">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                            <p className="text-gray-500 mt-2">Loading IPD lab tests...</p>
                        </div>
                    ) : filteredTests.length === 0 ? (
                        <div className="bg-white rounded-lg shadow border border-gray-200 p-8 text-center">
                            <TestTube className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                            <h3 className="text-lg font-medium text-gray-900 mb-2">No IPD lab tests found</h3>
                            <p className="text-gray-500">There are no IPD lab tests matching your criteria.</p>
                        </div>
                    ) : (
                        <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Patient Name</TableHead>
                                        <TableHead>UHID</TableHead>
                                        <TableHead>Test Name</TableHead>
                                        <TableHead>Type</TableHead>
                                        <TableHead>Vendor</TableHead>
                                        <TableHead>Hospital Cost</TableHead>
                                        <TableHead>Patient price</TableHead>
                                        <TableHead>Priority</TableHead>
                                        <TableHead>Status</TableHead>
                                        <TableHead>Ordered Date</TableHead>
                                        <TableHead>Doctor</TableHead>
                                        <TableHead>Actions</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {filteredTests.map((test: any) => (
                                        <TableRow key={test.id}>
                                            <TableCell className="font-medium">
                                                {test.admission?.queue?.patient?.name || 'N/A'}
                                            </TableCell>
                                            <TableCell>{test.admission?.queue?.patient?.uhid || 'N/A'}</TableCell>
                                            <TableCell className="font-medium">{test.testName}</TableCell>
                                            <TableCell>
                                                <Badge variant="outline">
                                                    {String(test.processingType ?? "INTERNAL").toUpperCase() === "EXTERNAL" ? "External" : "Internal"}
                                                </Badge>
                                            </TableCell>
                                            <TableCell>
                                                {String(test.processingType ?? "INTERNAL").toUpperCase() === "EXTERNAL"
                                                    ? (test.externalVendor?.name ?? "—")
                                                    : "—"}
                                            </TableCell>
                                            <TableCell>
                                                {String(test.processingType ?? "INTERNAL").toUpperCase() === "EXTERNAL"
                                                    ? (typeof test.externalCost === "number" ? `₹${test.externalCost}` : "—")
                                                    : "—"}
                                            </TableCell>
                                            <TableCell className="font-medium">
                                                {typeof test.testCost === "number" ? `₹${test.testCost}` : "—"}
                                            </TableCell>
                                            <TableCell>
                                                <Badge className={getPriorityColor(test.priority)}>
                                                    {test.priority}
                                                </Badge>
                                            </TableCell>
                                            <TableCell>
                                                <Badge className={getStatusColor(test.status)}>
                                                    {test.status}
                                                </Badge>
                                            </TableCell>
                                            <TableCell>
                                                {new Date(test.orderedAt).toLocaleDateString()}
                                            </TableCell>
                                            <TableCell>
                                                {test.orderedBy?.name || 'N/A'}
                                            </TableCell>
                                            <TableCell>
                                                {!adminMode && test.status === 'ORDERED' && (
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={() => handleStatusUpdate(test)}
                                                    >
                                                        Start Processing
                                                    </Button>
                                                )}
                                                {!adminMode && test.status === 'ORDERED' && (
                                                    <Button
                                                        size="sm"
                                                        variant="ghost"
                                                        className="ml-2 text-red-600 hover:text-red-700"
                                                        disabled={deleteTestMutation.isPending}
                                                        onClick={() => {
                                                            if (!window.confirm(`Delete test "${test.testName}"?`)) return;
                                                            deleteTestMutation.mutate(test.id);
                                                        }}
                                                    >
                                                        Delete
                                                    </Button>
                                                )}
                                                {!adminMode && (test.status === 'IN_PROGRESS' || test.status === 'SCHEDULED') && (
                                                    <Button
                                                        size="sm"
                                                        onClick={() => handleStatusUpdate(test)}
                                                    >
                                                        Enter Results
                                                    </Button>
                                                )}
                                                {(adminMode || test.status !== 'COMPLETED') && (
                                                    <Button
                                                        size="sm"
                                                        variant={adminMode ? "outline" : "ghost"}
                                                        className={adminMode ? "" : "ml-2"}
                                                        onClick={() => {
                                                            setEditTest(test);
                                                            const pt =
                                                                String(test.processingType ?? "INTERNAL").toUpperCase() ===
                                                                "EXTERNAL"
                                                                    ? "EXTERNAL"
                                                                    : "INTERNAL";
                                                            setEditProcessingType(pt);
                                                            setEditExternalVendorId(
                                                                test.externalVendor?.id ?? test.externalVendorId ?? ""
                                                            );
                                                            setEditExternalCost(
                                                                test.externalCost != null ? String(test.externalCost) : ""
                                                            );
                                                            setEditCustomerPrice(
                                                                test.testCost != null && typeof test.testCost === "number"
                                                                    ? String(test.testCost)
                                                                    : ""
                                                            );
                                                            setIsEditDialogOpen(true);
                                                        }}
                                                    >
                                                        Edit
                                                    </Button>
                                                )}
                                                {test.status === 'COMPLETED' && test.resultValue && (
                                                    <Button
                                                        size="sm"
                                                        variant="ghost"
                                                        onClick={() => {
                                                            setSelectedTest(test);
                                                            setIsResultDialogOpen(true);
                                                            setResultData({
                                                                resultValue: test.resultValue || '',
                                                                resultUnit: test.resultUnit || '',
                                                                normalRange: test.normalRange || '',
                                                                abnormalFlag: test.abnormalFlag || false,
                                                                resultNotes: test.resultNotes || '',
                                                            });
                                                            setUploadedFiles(test.attachments?.map((att: any) => ({
                                                                id: att.id,
                                                                name: att.fileName,
                                                                url: att.fileUrl
                                                            })) || []);
                                                        }}
                                                    >
                                                        <Eye className="h-4 w-4 mr-1" />
                                                        View Results
                                                    </Button>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    )}
                </>
            )}

            <Dialog
                open={isEditDialogOpen}
                onOpenChange={(open) => {
                    setIsEditDialogOpen(open);
                    if (!open) setEditTest(null);
                }}
            >
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle>Edit test</DialogTitle>
                    </DialogHeader>
                    {!editTest ? (
                        <div className="text-sm text-artis-slate">No test selected.</div>
                    ) : (
                        <div className="space-y-4">
                            <div className="text-sm text-gray-700">
                                <div className="font-medium">
                                    {editTest.testName ?? editTest.labTest?.name ?? "Test"}
                                </div>
                                <div className="text-xs text-gray-500">
                                    {editTest.admission?.queue?.patient?.name ?? "Patient"} ·{" "}
                                    {editTest.admission?.queue?.patient?.uhid ?? "UHID —"}
                                </div>
                            </div>

                            <div>
                                <Label className="text-sm font-medium">Type</Label>
                                <Select
                                    value={editProcessingType}
                                    onValueChange={(v: "INTERNAL" | "EXTERNAL") =>
                                        setEditProcessingType(v)
                                    }
                                >
                                    <SelectTrigger className="mt-1 w-full">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="INTERNAL">Internal (in-house)</SelectItem>
                                        <SelectItem value="EXTERNAL">External (outside lab)</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>

                            <div>
                                <Label className="text-sm font-medium">Patient price (₹)</Label>
                                <Input
                                    className="mt-1"
                                    type="number"
                                    inputMode="decimal"
                                    min={0}
                                    step={0.01}
                                    placeholder="Amount billed to patient on IPD bill"
                                    value={editCustomerPrice}
                                    onChange={(e) => setEditCustomerPrice(e.target.value)}
                                />
                                <p className="mt-1 text-xs text-muted-foreground">
                                    {adminMode
                                        ? "Saved value becomes the patient charge on the IPD revenue bill (including completed / frozen bills when admin saves)."
                                        : "Shown on reception IPD billing after save (bill must be in draft for amounts to refresh)."}
                                </p>
                            </div>

                            {editProcessingType === "EXTERNAL" && (
                                <>
                                    <div>
                                        <Label className="text-sm font-medium">External Vendor</Label>
                                        <Select
                                            value={editExternalVendorId || "__none__"}
                                            onValueChange={(v) =>
                                                setEditExternalVendorId(v === "__none__" ? "" : v)
                                            }
                                        >
                                            <SelectTrigger className="mt-1 w-full">
                                                <SelectValue placeholder="Select external vendor" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="__none__">Select vendor</SelectItem>
                                                {externalVendors.length === 0 ? (
                                                    <SelectItem value="__empty__" disabled>
                                                        No external vendors
                                                    </SelectItem>
                                                ) : (
                                                    externalVendors.map((v: any) => (
                                                        <SelectItem key={v.id} value={v.id}>
                                                            {v.name}
                                                        </SelectItem>
                                                    ))
                                                )}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div>
                                        <Label className="text-sm font-medium">Hospital Cost (₹)</Label>
                                        <Input
                                            className="mt-1"
                                            type="number"
                                            inputMode="decimal"
                                            placeholder="e.g. 250"
                                            value={editExternalCost}
                                            onChange={(e) => setEditExternalCost(e.target.value)}
                                        />
                                    </div>
                                </>
                            )}
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
                            Cancel
                        </Button>
                        <Button
                            disabled={!editTest || updateTestMetaMutation.isPending}
                            onClick={() => {
                                if (!editTest) return;
                                if (
                                    !adminMode &&
                                    String(editTest.status ?? "").toUpperCase() === "COMPLETED"
                                ) {
                                    toast.error("Completed tests cannot be edited");
                                    return;
                                }
                                const custRaw = editCustomerPrice.trim();
                                const custParsed = custRaw === "" ? NaN : Number(custRaw);
                                if (custRaw !== "" && (!Number.isFinite(custParsed) || custParsed < 0)) {
                                    toast.error("Enter a valid patient price or leave blank");
                                    return;
                                }
                                const testCostPayload = custRaw === "" ? null : custParsed;

                                if (editProcessingType === "EXTERNAL") {
                                    if (!editExternalVendorId) {
                                        toast.error("Select external vendor");
                                        return;
                                    }
                                    const costNum = Number(editExternalCost);
                                    if (!Number.isFinite(costNum) || costNum <= 0) {
                                        toast.error("Enter hospital cost");
                                        return;
                                    }
                                    updateTestMetaMutation.mutate({
                                        id: editTest.id,
                                        data: {
                                            processingType: "EXTERNAL",
                                            externalVendorId: editExternalVendorId,
                                            externalCost: costNum,
                                            testCost: testCostPayload,
                                        },
                                    });
                                } else {
                                    updateTestMetaMutation.mutate({
                                        id: editTest.id,
                                        data: {
                                            processingType: "INTERNAL",
                                            externalVendorId: null,
                                            externalCost: null,
                                            externalInvoiceId: null,
                                            testCost: testCostPayload,
                                        },
                                    });
                                }
                            }}
                        >
                            Save
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Order tests for admitted IPD patient */}
            <Dialog
                open={orderDialogOpen}
                onOpenChange={(open) => {
                    setOrderDialogOpen(open);
                    if (!open) {
                        setOrderTestSearch("");
                    }
                }}
            >
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>
                            Order lab tests
                            {selectedAdmission && (
                                <span className="block text-sm font-normal text-gray-500">
                                    {selectedAdmission.queue?.patient?.name} ·{" "}
                                    {selectedAdmission.queue?.patient?.uhid ?? "UHID —"} ·{" "}
                                    {selectedAdmission.queue?.ipdNumber ?? selectedAdmission.id}
                                </span>
                            )}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <Label className="text-sm font-medium">Test Type</Label>
                                <Select
                                    value={orderProcessingType}
                                    onValueChange={(v: "INTERNAL" | "EXTERNAL") => setOrderProcessingType(v)}
                                >
                                    <SelectTrigger className="mt-1 w-full">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="INTERNAL">Internal (in-house)</SelectItem>
                                        <SelectItem value="EXTERNAL">External (outside lab)</SelectItem>
                                    </SelectContent>
                                </Select>
                                {orderProcessingType === "EXTERNAL" && (
                                    <p className="mt-1 text-xs text-gray-500">
                                        External cost is hospital expense and does not change patient billing amount.
                                    </p>
                                )}
                            </div>
                            {orderProcessingType === "EXTERNAL" && (
                                <div>
                                    <div className="flex items-center justify-between">
                                        <Label className="text-sm font-medium">External Vendor</Label>
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            onClick={() => setVendorDialogOpen(true)}
                                        >
                                            Add vendor
                                        </Button>
                                    </div>
                                    <Select value={selectedExternalVendorId} onValueChange={setSelectedExternalVendorId}>
                                        <SelectTrigger className="mt-1 w-full">
                                            <SelectValue placeholder="Select external vendor" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {externalVendors.length === 0 ? (
                                                <SelectItem value="__none" disabled>No external vendors</SelectItem>
                                            ) : (
                                                externalVendors.map((v: any) => (
                                                    <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                                                ))
                                            )}
                                        </SelectContent>
                                    </Select>
                                    <div className="mt-2 flex items-center justify-between">
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            disabled={!selectedExternalVendorId}
                                            onClick={() => {
                                                setInvoiceVendorId(selectedExternalVendorId);
                                                setInvoiceDialogOpen(true);
                                            }}
                                        >
                                            Upload vendor invoice
                                        </Button>
                                        {selectedExternalVendorId && (
                                            <span className="text-xs text-gray-500">
                                                {externalVendorsById.get(selectedExternalVendorId)?.gstNo ? `GST: ${externalVendorsById.get(selectedExternalVendorId)?.gstNo}` : ""}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                        <div>
                            <Label className="text-sm font-medium">Select tests</Label>
                            <div className="relative mt-2">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                                <Input
                                    type="text"
                                    value={orderTestSearch}
                                    onChange={(e) => setOrderTestSearch(e.target.value)}
                                    placeholder="Search tests by name or code..."
                                    className="pl-10"
                                />
                            </div>
                            <div className="mt-2 border rounded-md max-h-64 overflow-y-auto divide-y">
                                {labTestsMaster.length === 0 ? (
                                    <div className="p-3 text-sm text-artis-slate">
                                        No lab master tests configured.
                                    </div>
                                ) : filteredLabMasterTests.length === 0 ? (
                                    <div className="p-3 text-sm text-artis-slate">
                                        No tests match your search.
                                    </div>
                                ) : (
                                    filteredLabMasterTests.map((test: any) => {
                                        const checked = selectedLabTestIds.includes(test.id);
                                        return (
                                            <button
                                                key={test.id}
                                                type="button"
                                                className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-gray-50"
                                                onClick={() => {
                                                    setSelectedLabTestIds((prev) =>
                                                        checked
                                                            ? prev.filter((id) => id !== test.id)
                                                            : [...prev, test.id]
                                                    );
                                                }}
                                            >
                                                <Checkbox
                                                    checked={checked}
                                                    onClick={(e) => e.stopPropagation()}
                                                    onCheckedChange={(val) => {
                                                        setSelectedLabTestIds((prev) =>
                                                            val
                                                                ? prev.includes(test.id)
                                                                    ? prev
                                                                    : [...prev, test.id]
                                                                : prev.filter((id) => id !== test.id)
                                                        );
                                                    }}
                                                />
                                                <div className="flex flex-col">
                                                    <span className="text-sm font-medium">
                                                        {test.name}
                                                    </span>
                                                    <span className="text-xs text-gray-500">
                                                        {test.code} · ₹{test.charge ?? 0}
                                                    </span>
                                                    {orderProcessingType === "EXTERNAL" && checked && (
                                                        <div className="mt-2 flex items-center gap-2">
                                                            <span className="text-xs text-gray-600">Hospital cost (₹):</span>
                                                            <Input
                                                                type="number"
                                                                inputMode="decimal"
                                                                placeholder="e.g. 250"
                                                                value={externalCostByTestId[test.id] ?? ""}
                                                                onClick={(e) => e.stopPropagation()}
                                                                onMouseDown={(e) => e.stopPropagation()}
                                                                onKeyDown={(e) => e.stopPropagation()}
                                                                onChange={(e) => {
                                                                    const val = e.target.value;
                                                                    setExternalCostByTestId((prev) => ({ ...prev, [test.id]: val }));
                                                                }}
                                                                className="h-8 w-32"
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                            </button>
                                        );
                                    })
                                )}
                            </div>
                            {selectedLabTestIds.length > 0 && (
                                <p className="mt-1 text-xs text-gray-500">
                                    {selectedLabTestIds.length} test(s) selected
                                </p>
                            )}
                        </div>
                        <div>
                            <Label className="text-sm font-medium">Priority</Label>
                            <Select
                                value={orderPriority}
                                onValueChange={(v: "ROUTINE" | "URGENT" | "STAT") =>
                                    setOrderPriority(v)
                                }
                            >
                                <SelectTrigger className="mt-1 w-40">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="ROUTINE">Routine</SelectItem>
                                    <SelectItem value="URGENT">Urgent</SelectItem>
                                    <SelectItem value="STAT">Stat</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => {
                                setOrderDialogOpen(false);
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            disabled={
                                ordering || !selectedAdmission || selectedLabTestIds.length === 0
                            }
                            onClick={async () => {
                                if (!selectedAdmission || selectedLabTestIds.length === 0) {
                                    toast.error("Select at least one test");
                                    return;
                                }
                                if (orderProcessingType === "EXTERNAL") {
                                    if (!selectedExternalVendorId) {
                                        toast.error("Select external vendor");
                                        return;
                                    }
                                    for (const id of selectedLabTestIds) {
                                        const c = Number(externalCostByTestId[id] ?? "");
                                        if (!Number.isFinite(c) || c <= 0) {
                                            toast.error("Enter hospital cost for selected external tests");
                                            return;
                                        }
                                    }
                                }
                                try {
                                    setOrdering(true);
                                    const admissionId = selectedAdmission.id as string;
                                    const tests = labTestsMaster.filter((t: any) =>
                                        selectedLabTestIds.includes(t.id)
                                    );
                                    for (const test of tests) {
                                        await ipdApi.createIPDLabTest({
                                            admissionId,
                                            testName: test.name,
                                            testCode: test.code,
                                            category: test.sampleType,
                                            priority: orderPriority,
                                            testCost: test.charge,
                                            labTestId: test.id,
                                            processingType: orderProcessingType,
                                            externalVendorId: orderProcessingType === "EXTERNAL" ? selectedExternalVendorId : undefined,
                                            externalCost: orderProcessingType === "EXTERNAL" ? Number(externalCostByTestId[test.id]) : undefined,
                                        });
                                    }
                                    queryClient.invalidateQueries({
                                        queryKey: ["ipd-lab-tests"],
                                    });
                                    toast.success(
                                        `${tests.length} test(s) ordered for this IPD admission`
                                    );
                                    setOrderDialogOpen(false);
                                    setSelectedLabTestIds([]);
                                    setOrderProcessingType("INTERNAL");
                                    setSelectedExternalVendorId("");
                                    setExternalCostByTestId({});
                                } catch (err: any) {
                                    console.error("Failed to order IPD lab tests", err);
                                    toast.error(
                                        err?.response?.data?.message ||
                                            "Failed to order lab tests"
                                    );
                                } finally {
                                    setOrdering(false);
                                }
                            }}
                        >
                            {ordering ? "Ordering..." : "Order tests"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Add external vendor dialog */}
            <Dialog open={vendorDialogOpen} onOpenChange={setVendorDialogOpen}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle>Add external lab vendor</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <Label>Name *</Label>
                            <Input value={newVendorForm.name} onChange={(e) => setNewVendorForm((p) => ({ ...p, name: e.target.value }))} />
                        </div>
                        <div>
                            <Label>Phone</Label>
                            <Input value={newVendorForm.phone} onChange={(e) => setNewVendorForm((p) => ({ ...p, phone: e.target.value }))} />
                        </div>
                        <div>
                            <Label>Address</Label>
                            <Input value={newVendorForm.address} onChange={(e) => setNewVendorForm((p) => ({ ...p, address: e.target.value }))} />
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div>
                                <Label>GST No</Label>
                                <Input value={newVendorForm.gstNo} onChange={(e) => setNewVendorForm((p) => ({ ...p, gstNo: e.target.value }))} />
                            </div>
                            <div>
                                <Label>Email</Label>
                                <Input value={newVendorForm.email} onChange={(e) => setNewVendorForm((p) => ({ ...p, email: e.target.value }))} />
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setVendorDialogOpen(false)}>Cancel</Button>
                        <Button
                            disabled={creatingVendor || !newVendorForm.name.trim()}
                            onClick={async () => {
                                try {
                                    setCreatingVendor(true);
                                    await ipdApi.createLabExternalVendor({
                                        name: newVendorForm.name.trim(),
                                        phone: newVendorForm.phone.trim() || undefined,
                                        address: newVendorForm.address.trim() || undefined,
                                        gstNo: newVendorForm.gstNo.trim() || undefined,
                                        email: newVendorForm.email.trim() || undefined,
                                    });
                                    toast.success("Vendor added");
                                    setNewVendorForm({ name: "", phone: "", address: "", gstNo: "", email: "" });
                                    setVendorDialogOpen(false);
                                    await refetchExternalVendors();
                                } catch (e: any) {
                                    toast.error(e?.response?.data?.message || "Failed to add vendor");
                                } finally {
                                    setCreatingVendor(false);
                                }
                            }}
                        >
                            {creatingVendor ? "Saving..." : "Save"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Upload external vendor invoice dialog */}
            <Dialog open={invoiceDialogOpen} onOpenChange={setInvoiceDialogOpen}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle>Upload external vendor invoice</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div className="text-sm text-gray-600">
                            Vendor: <span className="font-medium text-gray-900">{externalVendorsById.get(invoiceVendorId)?.name ?? "—"}</span>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div>
                                <Label>Invoice No *</Label>
                                <Input value={invoiceForm.invoiceNumber} onChange={(e) => setInvoiceForm((p) => ({ ...p, invoiceNumber: e.target.value }))} />
                            </div>
                            <div>
                                <Label>Invoice Date *</Label>
                                <Input type="date" value={invoiceForm.invoiceDate} onChange={(e) => setInvoiceForm((p) => ({ ...p, invoiceDate: e.target.value }))} />
                            </div>
                        </div>
                        <div>
                            <Label>Total Amount (₹) *</Label>
                            <Input
                                type="number"
                                inputMode="decimal"
                                value={invoiceForm.totalAmount}
                                onChange={(e) => setInvoiceForm((p) => ({ ...p, totalAmount: e.target.value }))}
                            />
                        </div>
                        <div>
                            <Label>Notes</Label>
                            <Textarea value={invoiceForm.notes} onChange={(e) => setInvoiceForm((p) => ({ ...p, notes: e.target.value }))} rows={2} />
                        </div>
                        <div>
                            <Label>Invoice file (optional)</Label>
                            <Input
                                type="file"
                                accept=".pdf,.jpg,.jpeg,.png"
                                onChange={(e) => setInvoiceFile(e.target.files?.[0] ?? null)}
                            />
                            {invoiceFile && (
                                <p className="mt-1 text-xs text-gray-500">Selected: {invoiceFile.name}</p>
                            )}
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setInvoiceDialogOpen(false)}>Cancel</Button>
                        <Button
                            disabled={
                                uploadingInvoice ||
                                !invoiceVendorId ||
                                !invoiceForm.invoiceNumber.trim() ||
                                !invoiceForm.invoiceDate ||
                                !(Number(invoiceForm.totalAmount) > 0)
                            }
                            onClick={async () => {
                                try {
                                    setUploadingInvoice(true);
                                    const fd = new FormData();
                                    fd.append("invoiceNumber", invoiceForm.invoiceNumber.trim());
                                    fd.append("invoiceDate", invoiceForm.invoiceDate);
                                    fd.append("totalAmount", String(Number(invoiceForm.totalAmount)));
                                    if (invoiceForm.notes.trim()) fd.append("notes", invoiceForm.notes.trim());
                                    if (invoiceFile) fd.append("file", invoiceFile);
                                    await ipdApi.createLabExternalVendorInvoice(invoiceVendorId, fd);
                                    toast.success("Invoice uploaded");
                                    setInvoiceForm({ invoiceNumber: "", invoiceDate: "", totalAmount: "", notes: "" });
                                    setInvoiceFile(null);
                                    setInvoiceDialogOpen(false);
                                } catch (e: any) {
                                    toast.error(e?.response?.data?.message || "Failed to upload invoice");
                                } finally {
                                    setUploadingInvoice(false);
                                }
                            }}
                        >
                            {uploadingInvoice ? "Uploading..." : "Upload"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Result Entry Dialog */}
            <Dialog open={isResultDialogOpen} onOpenChange={setIsResultDialogOpen}>
                <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Enter Test Results - {selectedTest?.testName}</DialogTitle>
                    </DialogHeader>
                    {selectedTest && (
                        <div className="space-y-4">
                            <div className="bg-blue-50 p-4 rounded-lg">
                                <div className="grid grid-cols-2 gap-4 text-sm">
                                    <div>
                                        <span className="font-medium text-gray-700">Patient:</span>
                                        <span className="ml-2 text-gray-900">{selectedTest.admission?.queue?.patient?.name}</span>
                                    </div>
                                    <div>
                                        <span className="font-medium text-gray-700">UHID:</span>
                                        <span className="ml-2 text-gray-900">{selectedTest.admission?.queue?.patient?.uhid}</span>
                                    </div>
                                    <div>
                                        <span className="font-medium text-gray-700">Test Name:</span>
                                        <span className="ml-2 text-gray-900">{selectedTest.testName}</span>
                                    </div>
                                    <div>
                                        <span className="font-medium text-gray-700">Priority:</span>
                                        <Badge className={getPriorityColor(selectedTest.priority)}>
                                            {selectedTest.priority}
                                        </Badge>
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <Label htmlFor="resultValue">Result Value *</Label>
                                    <Input
                                        id="resultValue"
                                        value={resultData.resultValue}
                                        onChange={(e) => setResultData(prev => ({ ...prev, resultValue: e.target.value }))}
                                        placeholder="Enter result value"
                                        required
                                    />
                                </div>
                                <div>
                                    <Label htmlFor="resultUnit">Result Unit</Label>
                                    <Input
                                        id="resultUnit"
                                        value={resultData.resultUnit}
                                        onChange={(e) => setResultData(prev => ({ ...prev, resultUnit: e.target.value }))}
                                        placeholder="e.g., mg/dL, %"
                                    />
                                </div>
                                <div>
                                    <Label htmlFor="normalRange">Normal Range</Label>
                                    <Input
                                        id="normalRange"
                                        value={resultData.normalRange}
                                        onChange={(e) => setResultData(prev => ({ ...prev, normalRange: e.target.value }))}
                                        placeholder="e.g., 70-100"
                                    />
                                </div>
                                <div className="flex items-center gap-4 pt-6">
                                    <div className="flex items-center gap-2">
                                        <input
                                            type="checkbox"
                                            id="abnormalFlag"
                                            checked={resultData.abnormalFlag}
                                            onChange={(e) => setResultData(prev => ({ ...prev, abnormalFlag: e.target.checked }))}
                                            className="rounded"
                                        />
                                        <Label htmlFor="abnormalFlag" className="font-normal cursor-pointer">
                                            Abnormal Result
                                        </Label>
                                    </div>
                                </div>
                            </div>

                            <div>
                                <Label htmlFor="resultNotes">Result Notes</Label>
                                <Textarea
                                    id="resultNotes"
                                    value={resultData.resultNotes}
                                    onChange={(e) => setResultData(prev => ({ ...prev, resultNotes: e.target.value }))}
                                    placeholder="Additional notes or observations"
                                    rows={3}
                                />
                            </div>

                            {/* File Upload Section */}
                            <div>
                                <Label>Upload Reports/Documents</Label>
                                <div className="mt-2 border-2 border-dashed border-gray-300 rounded-lg p-4">
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        onChange={handleFileUpload}
                                        className="hidden"
                                        accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                                    />
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={() => fileInputRef.current?.click()}
                                        disabled={isUploading}
                                        className="w-full"
                                    >
                                        <Upload className="h-4 w-4 mr-2" />
                                        {isUploading ? 'Uploading...' : 'Select File'}
                                    </Button>
                                    {uploadedFiles.length > 0 && (
                                        <div className="mt-4 space-y-2">
                                            {uploadedFiles.map((file) => (
                                                <div key={file.id} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                                                    <div className="flex items-center gap-2">
                                                        <FileText className="h-4 w-4 text-gray-500" />
                                                        <span className="text-sm text-gray-700">{file.name}</span>
                                                        {file.file && (
                                                            <Badge variant="outline" className="text-xs">
                                                                Pending Upload
                                                            </Badge>
                                                        )}
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        {file.url && (
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                onClick={() => window.open(file.url, '_blank')}
                                                            >
                                                                <Eye className="h-4 w-4" />
                                                            </Button>
                                                        )}
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => handleRemoveFile(file.id)}
                                                        >
                                                            <X className="h-4 w-4" />
                                                        </Button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    {uploadedFiles.length > 0 && uploadedFiles.some(f => f.file) && (
                                        <p className="mt-2 text-xs text-gray-500">
                                            Files will be uploaded when you click "Save & Complete Test"
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setIsResultDialogOpen(false)}>
                            Cancel
                        </Button>
                        <Button onClick={handleSubmitResult}>
                            Save & Complete Test
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

